import type { Enums } from "@/lib/database.types";

/* W7 Reproducibility Review vocabulary (WORKSHOP.md §W7), shared by the
 * review action and both UIs. The check list is fixed: the AI assesses each
 * one as pass / fail / undetermined — never a naked score. */

export const REPRO_CHECKS = [
  { id: "required_software", label: "Required software" },
  { id: "tool_versions", label: "Tool versions" },
  { id: "authentication", label: "Authentication requirements" },
  { id: "permissions", label: "Permissions" },
  { id: "starting_directory", label: "Starting directory" },
  { id: "repository_state", label: "Starting repository/system state" },
  { id: "configuration", label: "Required configuration" },
  { id: "environment_variables", label: "Environment variables" },
  { id: "project_values", label: "Project-specific values (placeholder candidates)" },
  { id: "unexplained_changes", label: "Unexplained file changes" },
  { id: "missing_stages", label: "Missing restore/build/test/deploy steps" },
  { id: "success_conditions", label: "Clear success conditions" },
  { id: "untested_commands", label: "Untested commands" },
  { id: "machine_assumptions", label: "Author's-machine assumptions" },
  { id: "rollback_recovery", label: "Rollback / recovery" },
] as const;

export type ReproCheckId = (typeof REPRO_CHECKS)[number]["id"];
export type ReproCheckStatus = "pass" | "fail" | "undetermined";

export type ReproCheck = {
  id: ReproCheckId;
  status: ReproCheckStatus;
  /** What was found (or why it couldn't be determined). */
  note: string;
  /** The concrete edit that would fix it — required for fail/undetermined. */
  action: string | null;
  /** 1-based step numbers the issue lives in ([] = recipe-level). */
  stepRefs: number[];
};

export const CHECK_LABEL: Record<ReproCheckId, string> = Object.fromEntries(
  REPRO_CHECKS.map((c) => [c.id, c.label]),
) as Record<ReproCheckId, string>;

/* Statuses are earned by evidence or explicit author action (never
 * auto-granted): the `evidence` note says what unlocks each one. */
export const STATUS_META: Record<
  Enums<"recipe_status">,
  { label: string; description: string; evidence: string | null }
> = {
  draft: {
    label: "Draft",
    description: "Still being written — follow with care.",
    evidence: null,
  },
  needs_clarification: {
    label: "Needs clarification",
    description: "A review found gaps the author hasn't resolved yet.",
    evidence: null,
  },
  ready_for_testing: {
    label: "Ready for testing",
    description: "The author considers it complete but untested.",
    evidence: null,
  },
  tested_by_author: {
    label: "Tested by author",
    description: "The author has a successful recorded run — evidence from one environment.",
    evidence: "Requires a successful run of this recipe recorded by the author.",
  },
  independently_reproduced: {
    label: "Independently reproduced",
    description: "Someone other than the author followed it successfully and said so.",
    evidence: "Requires reproduction feedback marked “reproduced” from another user.",
  },
  verified: {
    label: "Verified",
    description: "Independently reproduced, with the environments it worked in on record.",
    evidence: "Requires independent reproduction feedback that names its environment.",
  },
  outdated: {
    label: "Outdated",
    description: "The author has flagged tool or version drift — verify before trusting.",
    evidence: null,
  },
};

export const STATUS_ORDER: Enums<"recipe_status">[] = [
  "draft",
  "needs_clarification",
  "ready_for_testing",
  "tested_by_author",
  "independently_reproduced",
  "verified",
  "outdated",
];
